import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

export type AppUser = { user_id: string; full_name: string; role: string; is_active: boolean }

type AuthState = {
  loading: boolean
  session: Session | null
  /** null = connecté mais absent (ou inactif) de la liste des personnes autorisées */
  appUser: AppUser | null
  signIn: (email: string, password: string) => Promise<string | null>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

async function loadAppUser(session: Session | null): Promise<AppUser | null> {
  if (!session) return null
  const { data, error } = await supabase
    .from('app_users')
    .select('user_id, full_name, role, is_active')
    .eq('user_id', session.user.id)
    .maybeSingle()
  if (error || !data || !data.is_active) return null
  return data as AppUser
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)
  const [appUser, setAppUser] = useState<AppUser | null>(null)

  useEffect(() => {
    let alive = true
    const apply = async (s: Session | null) => {
      const u = await loadAppUser(s)
      if (!alive) return
      setSession(s)
      setAppUser(u)
      setLoading(false)
    }
    supabase.auth.getSession().then(({ data }) => apply(data.session))
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      // Ne pas appeler Supabase directement dans ce rappel (recommandation Supabase).
      setTimeout(() => apply(s), 0)
    })
    return () => {
      alive = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    if (!error) return null
    if (error.message.toLowerCase().includes('invalid login')) return 'E-mail ou mot de passe incorrect.'
    if (error.message.toLowerCase().includes('fetch')) return 'Impossible de joindre le serveur. Vérifiez la connexion Internet.'
    return 'La connexion a échoué. Réessayez dans un instant.'
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ loading, session, appUser, signIn, signOut }}>{children}</AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>')
  return ctx
}
